/**
 * Admin Accounts API Client
 *
 * Provides methods for managing insurance accounts in admin/backoffice context.
 * All fetch methods unwrap the standard API envelope
 * { success, message, timestamp, data: T } and return T directly.
 */

import type {
  AdminAccount,
  AccountStatus,
  KycStatus,
} from "../types/admin-accounts.types";

// ─── Response types (unwrapped — match the `data` field of the API envelope) ─

export interface AdminAccountsResponse {
  accounts: AdminAccount[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// ─── Fetch infrastructure ─────────────────────────────────────────────────────

export type AdminAccountsFetchFunction = (
  url: string,
  options?: RequestInit,
) => Promise<Response>;

function defaultFetch(url: string, options?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
}

let _fetch: AdminAccountsFetchFunction = defaultFetch;

export function configureAdminAccountsApi(
  fetchFn: AdminAccountsFetchFunction,
): void {
  _fetch = fetchFn;
}

async function unwrap<T>(response: Response): Promise<T> {
  const body = await response.json();
  if (!response.ok) {
    throw new Error(
      body?.error?.message || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
  return body.data as T;
}

// ─── API client ───────────────────────────────────────────────────────────────

export const adminAccountsApi = {
  async fetchAll(
    params?: {
      limit?: number;
      offset?: number;
      status?: string;
      accountType?: string;
      kycStatus?: string;
      riskScore?: string;
      search?: string;
    },
    baseUrl = "/api/v1",
  ): Promise<AdminAccountsResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    if (params?.status) query.set("status", params.status);
    if (params?.accountType) query.set("account_type", params.accountType);
    if (params?.kycStatus) query.set("kyc_status", params.kycStatus);
    if (params?.riskScore) query.set("risk_score", params.riskScore);
    if (params?.search) query.set("search", params.search);
    const qs = query.toString();
    const res = await _fetch(`${baseUrl}/admin/accounts${qs ? `?${qs}` : ""}`);
    return unwrap<AdminAccountsResponse>(res);
  },

  async fetchById(
    accountId: string,
    baseUrl = "/api/v1",
  ): Promise<AdminAccount> {
    const res = await _fetch(`${baseUrl}/admin/accounts/${accountId}`);
    return unwrap<AdminAccount>(res);
  },

  async updateAccountStatus(
    accountId: string,
    status: AccountStatus,
    notes?: string,
    baseUrl = "/api/v1",
  ): Promise<AdminAccount> {
    const res = await _fetch(`${baseUrl}/admin/accounts/${accountId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status, notes }),
    });
    return unwrap<AdminAccount>(res);
  },

  async updateKycStatus(
    accountId: string,
    kycStatus: KycStatus,
    notes?: string,
    baseUrl = "/api/v1",
  ): Promise<AdminAccount> {
    const res = await _fetch(`${baseUrl}/admin/accounts/${accountId}/kyc`, {
      method: "PATCH",
      body: JSON.stringify({ kyc_status: kycStatus, notes }),
    });
    return unwrap<AdminAccount>(res);
  },

  async addNote(
    accountId: string,
    note: string,
    baseUrl = "/api/v1",
  ): Promise<AdminAccount> {
    const res = await _fetch(`${baseUrl}/admin/accounts/${accountId}/notes`, {
      method: "POST",
      body: JSON.stringify({ content: note }),
    });
    return unwrap<AdminAccount>(res);
  },
};
